import { generateUuid } from '@zurich-framework/core/utils';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { BusinessProcessIdProvider } from '@zurich/common/contexts';
import { useSessionStorage } from '@zurich/common/hooks';
import { vsgaStepConfig } from '~/components/stepConfig';
import { useGtm } from '~/hooks/useGtm';
import withSSR from '~/lib/ssr/hof';
import {
  payloadDataValidByStepValidationSelectorFamily,
  vsgaPayloadAtom,
} from '~/state';
import { VorschadenGutachtenStepper } from '~/views/VorschadenGutachtenStepper';

type VSGAProps = {
  stepName?: string;
};

export default function VSGA({ stepName }: VSGAProps) {
  const router = useRouter();
  const [formStep, setFormStep] = useState<string | undefined>();
  const payload = useRecoilValue(vsgaPayloadAtom);
  const [session, _] = useSessionStorage('vsga-payload', payload);

  const stepIndex = vsgaStepConfig.findIndex(
    (step) => step.stepName === stepName
  );

  let initialStep = 0;

  if (stepIndex > 0) {
    initialStep = stepIndex;
  }

  const invalidStep = useRecoilValue(
    payloadDataValidByStepValidationSelectorFamily({ activeStep: initialStep })
  );

  const [redirectStep, setRedirectStep] = useState(stepName);

  useGtm({ formStep });

  // Check if the data for the inital step passed as query param is valid, if not redirect to the first invalid step
  //FIXME: refactor VSGA query params/state handling to make this redundand
  useEffect(() => {
    if (invalidStep > 0) {
      setRedirectStep(vsgaStepConfig[invalidStep]?.stepName ?? stepName);
      void router.replace(
        `/vsga?step=${vsgaStepConfig[invalidStep]?.stepName}&id=${session?.id ?? generateUuid()}`
      );
    }

    // only run on initial mount and check
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BusinessProcessIdProvider
      businessProcessId={session?.id ?? generateUuid()}
    >
      <VorschadenGutachtenStepper
        stepName={redirectStep}
        onStepChange={(step) => {
          if (redirectStep) {
            setFormStep(redirectStep);
            return;
          }
          setFormStep(step.stepName);
        }}
      />
    </BusinessProcessIdProvider>
  );
}

export const getServerSideProps = withSSR()(
  {
    translations: ['vsga'],
    allowPublic: true,
  },
  async ({ query }) => {
    const props: VSGAProps = {};
    if (typeof query['step'] === 'string') {
      props['stepName'] = query['step'];
    }

    return Promise.resolve(props);
  }
);
